import { GovFormWeb } from '@gov-design-system-ce/react-form';
import type { AuthorizationType } from '@gov-nx/component/common/Autorizace/types';
import { useAuthorizationMethodsForm } from '@gov-nx/component/common/Autorizace/useAuthorizationMethodsForm';
import { useConfiguration } from '@gov-nx/core/service/configuration/useConfiguration';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import { FormInput } from '@gov-nx/ui/web/Form/FormInput';
import { FormRadio } from '@gov-nx/ui/web/Form/FormRadio';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import './styles.css';
import { MethodPanel } from './MethodPanel';
import { getRedirectUrl } from './utils';

interface AuthorizationMethodsProps {
	code: (typeof PageCode)['autorizace'];
	allowedAuthorizationTypes: AuthorizationType[];
}

/**
 * The whole authorization flow on a single screen (replaces the former
 * Step1 + Step2 wizard). A radio group lists the available methods; selecting
 * one expands an inline {@link MethodPanel} directly under the option with its
 * subtitle, action button and — after a code is sent — the code input.
 */
export const AuthorizationMethods = ({
	code,
	allowedAuthorizationTypes,
}: AuthorizationMethodsProps) => {
	const { autorizaceNiaViaLoginUrl } = useConfiguration();

	const {
		form,
		selected,
		identityUrl,
		codeSent,
		sending,
		submitting,
		resendSecondsLeft,
		onAction,
		handleConfirmSubmit,
	} = useAuthorizationMethodsForm({
		code,
		allowedAuthorizationTypes,
		onRedirectAuthorization: (ukonId) => {
			window.location.href = getRedirectUrl(autorizaceNiaViaLoginUrl, ukonId);
		},
	});

	// Render the inline panel only under the currently selected option.
	form.fields.authorizationType.field.options =
		form.fields.authorizationType.field.options.map((option) => {
			if (option.value !== selected) {
				return option;
			}
			const type = option.value;
			return {
				...option,
				children: (
					<MethodPanel
						code={code}
						type={type}
						identityUrl={identityUrl}
						codeSent={codeSent}
						sending={sending}
						resendSecondsLeft={resendSecondsLeft}
						submitting={submitting}
						codeInput={
							// The design shows no visible label above the code input —
							// the placeholder describes it. Keep the label text in the
							// DOM but visually hidden so screen readers still announce it.
							<FormInput
								{...form.fields.oneTimePassword}
								label={{
									...form.fields.oneTimePassword.label,
									className: 'sr-only',
								}}
							/>
						}
						onAction={() => onAction(type)}
						onResend={() => onAction(type)}
					/>
				),
			};
		});

	return (
		<GovFormWeb
			formDefinition={form}
			onSubmit={handleConfirmSubmit}>
			{/*
			 * .autorizace-method-options (see styles.css) bumps each radio option
			 * label from the size-m default (body-m) to body-l. A plain CSS class is
			 * used instead of a Tailwind arbitrary-variant utility because the latter
			 * was not reliably emitted in the app build; the dedicated rule is always
			 * present and overrides the unlayered DS label rule.
			 */}
			<div className="autorizace-method-options">
				{/*
				 * size="xl" renders the legend at --font-size-body-xl (20px).
				 * mb-(--spacing-s)! (8px) spaces the legend from the options —
				 * important because `:is(gov-form-control) * { margin-bottom: 0 }`
				 * (unlayered) zeroes the legend's margin otherwise.
				 * gap="m" (--spacing-m = 16px) spaces out the authorization options.
				 */}
				<FormRadio
					{...form.fields.authorizationType}
					label={{
						...form.fields.authorizationType.label,
						size: 'xl',
						className: 'mb-(--spacing-s)!',
						children: (
							// The legend holds phrasing content and carries its own
							// typography, so paragraphs render as inline spans.
							<MarkdownRender
								disableWrapper
								components={{ p: ({ children }) => <span>{children}</span> }}>
								{form.fields.authorizationType.label.children ?? ''}
							</MarkdownRender>
						),
					}}
					group={{ gap: 'm' }}
				/>
			</div>
		</GovFormWeb>
	);
};
